/* eslint-disable jsx-a11y/heading-has-content */
/* eslint-disable no-shadow */
import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import { ReactAux, Section } from '@jkhy/vsg-design-system';
import { Base, BaseType as BaseType_ } from '../common/base-component';

import { getRequiredDocuments } from '../../redux/actions/doc-actions';
import { startLoading, endLoading } from '../../redux/actions/loading-actions';
import { AppState as AppState_ } from '../../redux/app-state';
import RequiredDocument_, { Attachment as Attachment_ } from '../../data/models/RequiredDocumet';
import { CountingModel as CountingModel_, renewalDocumentsCountFetched } from '../../redux/actions/counts-qd-enabled-actions';
import { setAntivirusResponse } from '../../redux/actions/anti-virus-actions';
import AntivirusResponseModel_ from '../../data/models/AntivirusResponse';
import { AntivirusResponseStatus as avStatuses_ } from '../../data/models/Enum';
import DocAPI from '../../data/doc-api';
import { formatDate } from '../../utils/helper';
import FileUploadCardWrapper from '../common/file-upload/file-upload';
import PublicContent from '../../data/models/PublicContent';
import { AppDispatch } from '../../data/models/AppDispatch';
import '../../../css/default.css';

type RequiredDocumentsProps = {
  docs: RequiredDocument_[],
  renewalDocumentsCountFetched: (params: CountingModel_) => void,
  getRequiredDocuments: () => void,
  startLoading: () => void,
  endLoading: () => void,
  setAntivirusResponse: (params: AntivirusResponseModel_) => void,
} & BaseType_

export type UploadCardFile = {
  error: string | null
  file: File
}

export type FileState = {
  fileRef: any
}

export type DocDetail = {
  title: string,
  maxVisibleTexts?: number,
  text: string | string[],
  isInvalid?: boolean,
}

export const PageDataUI = 'RequiredDocuments';

export class RequiredDocuments extends Base<RequiredDocumentsProps, FileState> {
  constructor(props) {
    super(props);

    this.state = {
      fileRef: {},
    };
  }

  componentDidMount = async () => {
    const { getRequiredDocuments, } = this.props;
    getRequiredDocuments();
  };

  processResult = result => {
    result.map(r => {
      const row = r;
      row.declinedReason = row.declinedReason || Object.create({});

      if (row.dueDate) {
        row.dueDate = new Date(row.dueDate);
        row.dateCreated = new Date(row.dateCreated);
        row.dateUpdated = new Date(row.dateUpdated);

        if (row.dueDate > new Date()) {
          row.header = 'bg-success fg-white';
          row.headerTitle = 'Coming Due';
        } else if (
          row.dueDate.getDate() === new Date().getDate()
          && row.dueDate.getMonth() === new Date().getMonth()
          && row.dueDate.getFullYear() === new Date().getFullYear()) {
          row.header = 'bg-brightyellow fg-white';
          row.headerTitle = 'Due Now';
        } else if (row.dueDate < new Date()) {
          row.header = 'bg-red fg-white';
          row.headerTitle = 'Past Due';
        }
      }
      return r;
    });
    result.sort((a, b) => {
      if (a.dueDate === null) {
        return 1;
      }
      if (b.dueDate === null) {
        return -1;
      }

      return a.dueDate.getTime() < b.dueDate.getTime() ? -1 : 1;
    });
    return result;
  };

  cutLongerLabels = (label, cutLength) => {
    if (label?.length > cutLength) {
      return `${label.substring(0, cutLength)}...`;
    }

    return label;
  }

  getDetails = (doc: RequiredDocument_) => {
    const details: DocDetail[] = [];
    const description = doc.portalDescription || doc.documentDescription;

    if (doc.status === 7) {
      details.push({ title: 'Declined on:', text: formatDate(doc.declinedReason.dateCreated), isInvalid: true, });
    }

    const requiredForText = doc.requiredFor.split(',').map(item => item.trim());

    details.push(
      {
        title: 'Required for:',
        maxVisibleTexts: requiredForText.length > 5 ? 5 : undefined,
        text: requiredForText.map(e => e.trim().replace(/&#(\d+);/g, (_, dec) => String.fromCharCode(dec))),
      }
    );

    if (doc.statementDate && !doc.hideStatementDate) {
      details.push({ title: 'Statement Date:', text: formatDate(doc.statementDate), });
    }

    if (description && !doc.hideDescription) {
      details.push({ title: 'Description:', text: description, });
    }

    if (doc.status !== 7 && doc.dueDate && !doc.hideDueDate) {
      details.push({ title: 'Due:', text: formatDate(doc.dueDate), });
    }

    return details;
  }

  getAllRequestsCount = async () => {
    const { renewalDocumentsCountFetched, } = this.props;

    const { data: reqDocCount, } = await DocAPI.getAllRequestsCount();
    renewalDocumentsCountFetched({ isFetched: true, count: reqDocCount, });
  };

  getFileRef = (id: number) => { return this.state.fileRef[id]; }

  onSubmitCallback = async () => {
    await this.getAllRequestsCount();
  }

  onUploadCallback = (docGuid: string, newAttachments: Attachment_[]) => {
    const { setAntivirusResponse, } = this.props;

    const hasThreat = newAttachments.find(a => (a.error === avStatuses_.FileNotFound || a.error === avStatuses_.ThreatFound));
    if (hasThreat) {
      const params: AntivirusResponseModel_ = { FileName: hasThreat.file.name, HasThreat: true, };
      setAntivirusResponse(params);
    }
  };

  content = () => {
    const documents = this.processResult(this.props.docs);
    const { requestDocsPageTitle, requestDocsPageDescription, } = window?.PublicContent ?? new PublicContent();
    return (
      <ReactAux>
        <Section dataUI={PageDataUI}>
          <h1 data-ui={`${PageDataUI}-page-title`} className="mb-2 text-preline break-word">{requestDocsPageTitle}</h1>
          <p data-ui={`${PageDataUI}-page-description`} className="mb-3 text-preline break-word">{requestDocsPageDescription}</p>

          {
            documents.map((doc: RequiredDocument_, i: number) => {
              const declinedReason = doc?.status === 7 && doc?.declinedReason?.rejectReason ? doc?.declinedReason?.rejectReason : '';
              const title = this.cutLongerLabels(doc.portalDocumentName || doc.documentType, 50);
              const key = `${doc?.id}-${title}-${i}`;

              return (
                <FileUploadCardWrapper
                  dataUI={`RequiredDocumentsFileUploadCard${i}`}
                  key={key}
                  keyValue={key}
                  className={documents.length - 1 === i ? '' : 'mb-3'}
                  isMultiple
                  cardErrorMessage={declinedReason}
                  title={title}
                  details={this.getDetails(doc)}
                  onRef={(ref) => {
                    this.setState(
                      (prevState) => ({ ...prevState, fileRef: { ...prevState.fileRef, [doc.id]: ref, }, })
                    );
                  }}
                  getRef={(docId: number) => { return this.getFileRef(docId); }}
                  document={doc}
                  onSubmitFileCallback={() => this.onSubmitCallback()}
                  onFileUploadCallback={this.onUploadCallback}
                  additionalInfoText="Each file must be less than 50MB. The total size of all files cannot be greater than 100MB."
                />
              );
            })
          }
        </Section>
      </ReactAux>
    );
  }
}

export default withRouter(connect(
  (state: AppState_) => ({
    docs: state.doc,
  }),
  (dispatch: AppDispatch) => {
    return {
      renewalDocumentsCountFetched: (params: CountingModel_) => dispatch(renewalDocumentsCountFetched(params)),
      getRequiredDocuments: () => dispatch(getRequiredDocuments()),
      startLoading: () => dispatch(startLoading()),
      endLoading: () => dispatch(endLoading()),
      setAntivirusResponse: (params: AntivirusResponseModel_) => dispatch(setAntivirusResponse(params)),
    };
  }
)(RequiredDocuments));
